import {NAMES} from "@/scripts/components/ab-lens-compatibility/constants/abLensCompatibility.constants";
import {AbLoaderInModal} from "@/scripts/components/ab-loader/ab-loader-in-modal.class";
import {ProductNameResponseItemDTO, ProductResponseDTO} from "@shared/DTO";
import ProductsApi from "@/scripts/api/products.api";
import { getBestCompatibility,  getLensCompatibilityRules} from "@/scripts/components/ab-lens-compatibility/abLensCompatibility.functions";
import { buildCameraCheckArea, buildFeedbackMessage, buildMainArea } from "@/scripts/components/ab-lens-compatibility/abLensCompatibility.html";
import "./abLensCompatibility.style.scss";
import {createDomElementHandler} from "@/scripts/utils/dom.utils";
import {
  AbSelectChangeDetail,
  AbSelectElement, AbSelectOption,
  AbSelectSearchDetail
} from "@/scripts/components/ab-select/ab-select.types";
import {Mount, ProductTypeEnum} from "@shared/enums";
import {filterOptionsByTerm} from "@/scripts/utils/filterProductByNames.util";
import {CompatibilityLevel} from "@/scripts/components/ab-lens-compatibility/abLensCompatibility.enums";
import {AbLensCompatibilityUpdateEvDetail} from "@/scripts/components/ab-lens-compatibility/abLensCompatibility.types";

export class AbLensCompatibilityElement extends HTMLElement {

  get feedbackWrapperEl() {
    return this.querySelector('.ab-lens-compatibility__feedback-wrapper') as HTMLElement;
  }

  private _productApiService = ProductsApi;

  #initialized: boolean = false;
  #loader: AbLoaderInModal;
  #lens: ProductResponseDTO;

  #clickEventHandler: (event: MouseEvent) => void;

  constructor() {
    super();
    this.#loader = new AbLoaderInModal();
  }

  async connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;
    this.bindEvents();
    this.init();
  }

  private bindEvents() {
    this.#clickEventHandler = (event: MouseEvent) => this.onClickOnDocument(event);
    document.addEventListener('click', this.#clickEventHandler);
  }

  private emitUpdatedViewEvent(
    lens: ProductResponseDTO,
    camera: ProductResponseDTO) {
    this.dispatchEvent(
      new CustomEvent<AbLensCompatibilityUpdateEvDetail>("ab-lens-compatibility-updated", {
        detail: { lens, camera },
        bubbles: true,
      }),
    );
  }

  private onClickOnDocument(event: MouseEvent) {

    const clickedElement = event.target as HTMLElement;
    if (!clickedElement) return;
    if (!this.contains(clickedElement)) return;

    event.stopPropagation();
    if (clickedElement.closest(`.${NAMES.TRIGGER_CHECK_CAMERA_OPEN}`) ) {
      this.showCameraCheckPanel();
      return;
    }
    if (clickedElement.closest(`.${NAMES.TRIGGER_CHECK_CAMERA_CLOSE}`)) {
      this.hideCameraCheckPanel();
      return;
    }

  }

  private buildCameraCheckPanel(
    allProducts: ProductNameResponseItemDTO[],
    lens: ProductResponseDTO,
    lensMountsRules: ReturnType<typeof getLensCompatibilityRules>
  ) {

    const allOptions: AbSelectOption[] = allProducts
      .map( (product) => {
        const {brand, names} = product?.productData || {};
        const group = brand?.length ? `Fotocamere ${brand}` : '';
        return {
          label: names?.[0],
          id: product._id,
          group
        }
      });

    const selectEl = createDomElementHandler(
      "ab-select",
      "ab-lens-compatibility__select",
      { "font-size": "sm" }
    ) as AbSelectElement;

    selectEl.options = allOptions;

    selectEl.addEventListener("ab-select-search", (event: Event) => {
      const term = ((event as CustomEvent<AbSelectSearchDetail>).detail?.term ?? "").toLowerCase();
      selectEl.options = filterOptionsByTerm(term, allOptions, allProducts);
    });

    selectEl.addEventListener("ab-select-change", (event: Event) => {
      const { option } = (event as CustomEvent<AbSelectChangeDetail>).detail ?? {};
      if (!option?.id) return;
      const lensMounts = lensMountsRules?.map( ({mount}) => mount?.public_id );
      this.checkCompatibility(lens, lensMounts, option.id);
    });

    return selectEl;

  }

  private showCameraCheckPanel() {
    this.resetFeedback();
    this.classList.add('ab-lens-compatibility--panel-open');
  }

  private hideCameraCheckPanel(options: { preserveFeedback?: boolean } = {}) {
    const { preserveFeedback = false } = options;
    this.emitUpdatedViewEvent(this.#lens, undefined);
    this.classList.remove('ab-lens-compatibility--panel-open');
    if (!preserveFeedback) {
      this.resetFeedback();
    }
  }

  private resetFeedback() {
    this.classList.remove('ab-lens-compatibility--feedback-shown');
    const tableRowFeedbackClasses = [
      NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_FULL,
      NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_PARTIAL
    ];

    tableRowFeedbackClasses.forEach( (rowClass) => {
      this.querySelectorAll(`.${rowClass}`)
        .forEach( (row) => {
          row.classList.remove(rowClass);
        });
    });

    this.feedbackWrapperEl.innerHTML = '';
  }

  private async checkCompatibility(
    lens: ProductResponseDTO,
    lensMounts: Mount[],
    cameraProductId: string
  ) {
    this.#loader.start();
    const camera = await this.fetchProduct(cameraProductId);
    this.#loader.stop();
    this.hideCameraCheckPanel({ preserveFeedback: true });
    if(!camera) return;
    this.emitUpdatedViewEvent(lens, camera);
    const mountId = camera?.cameraInterchangeableLensData?.mount;

    const bestCompatibility = getBestCompatibility(
      lensMounts,
      mountId
    );

    this.feedbackWrapperEl.replaceChildren(buildFeedbackMessage(lens, camera, bestCompatibility));
    this.classList.add('ab-lens-compatibility--feedback-shown');

    const tableRowFeedbackClasses = [
      NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_FULL,
      NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_PARTIAL
    ];

    tableRowFeedbackClasses.forEach( (rowClass) => {
      this.querySelectorAll(`.${rowClass}`)
        .forEach( (row) => {
          row.classList.remove(rowClass);
        });
    });

    if(bestCompatibility.compatibilityLevel === CompatibilityLevel.NOT_COMPATIBLE) return;
    const matchingRow = this.querySelector(`[data-mount="${bestCompatibility.mount}"]`);

    const classToAdd = bestCompatibility.compatibilityLevel === CompatibilityLevel.FULL
      ? NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_FULL
      : NAMES.MODIFIER_TABLE_ROW_COMPATIBILITY_PARTIAL;
    matchingRow?.classList.add(classToAdd);

  }

  private async init() {

    const productIds = this.getAttribute('productid');
    const shouldAutoOpen = this.shouldAutoOpenOnInit();
    if (!productIds) return;
    this.classList.add('ab-lens-compatibility');
    this.#loader.start();
    const [lens, productNames] = await Promise.all([
      this.fetchProduct(productIds),
      this.fetchProductNames()
    ]);
    this.#loader.stop();
    this.#lens = lens;
    this.buildDom(lens, productNames);
    this.emitUpdatedViewEvent(this.#lens, undefined);
    if(!shouldAutoOpen) return;
    this.showCameraCheckPanel();
  }

  private shouldAutoOpenOnInit() {
    const attr = this.getAttribute('auto-open');
    return attr !== null && (attr === '' || attr.toLowerCase() === 'true');
  }

  private buildDom(
    lens: ProductResponseDTO,
    productNames: ProductNameResponseItemDTO[]
  ) {

    const lensMountsRules = getLensCompatibilityRules(lens);

    if(!lensMountsRules?.at(0)?.mount) return;

    const mainAreaFragment = buildMainArea(lens, lensMountsRules);

    const cameraCheckAreaWrapper = buildCameraCheckArea();
    const elSelectElement = this.buildCameraCheckPanel(productNames, lens, lensMountsRules);
    cameraCheckAreaWrapper.appendChild(elSelectElement);
    this.replaceChildren(mainAreaFragment, cameraCheckAreaWrapper);
  }

  private async fetchProduct(productId: string) {
    return this._productApiService.getProduct(productId);
  }

  private async fetchProductNames() {
    return this._productApiService.getProductNames({
      type: [ProductTypeEnum.REFLEX, ProductTypeEnum.MIRRORLESS],
      visibility: ["showInCompatibilityTools"],
    });
  }

}

export function defineAbLensCompatibilityElement() {

  const TAG_NAME = NAMES.TAG_NAME;
  if (customElements.get(TAG_NAME)) return;

  customElements.define(TAG_NAME, AbLensCompatibilityElement);
}
